"use client";
import { useEffect, useState } from "react";

import { Body } from "@/types/common/Body";
import Image from "next/image";
import { useInView } from "react-intersection-observer";
import s from "./LazyImage.module.scss";

import { sanitizeFilename } from "@/utils/sanitizeFilename";
import { createImageSchema } from "@/lib/constants";

interface LazyImageProps {
    alt?: string;
    src: string;
    blurDataURL?: string;
    width: number;
    height: number;
    defaultInView?: boolean;
    disableOpacityAnimation?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    fill?: boolean;
    sizes?: string;
    caption?: Body[] | null;
    aspectRatio?: string;
    objectFit?: string;
    reference?: any;
    filename?: string;
    lqip?: string;
    onLoad?: () => void;
}



// Modifica tu componente LazyImage
export default function LazyImage({
    alt = "",
    src = "",
    blurDataURL = "",
    width,
    height,
    defaultInView = true,
    disableOpacityAnimation = false,
    fullHeight = false,
    fullWidth = false,
    fill = false,
    sizes = "",
    caption,
    aspectRatio,
    objectFit = "",
    reference,
    filename = "",
    lqip = ""
}: LazyImageProps) {
    const [loaded, setLoaded] = useState(false);
    const [inViewCheck, setInViewCheck] = useState(defaultInView);
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
        initialInView: defaultInView,
    });

    useEffect(() => {
        setInViewCheck(inView);
    }, [inView]);

    const filenameSanitized = sanitizeFilename(filename);


    const imageSchema = createImageSchema({
        src,
        alt: filenameSanitized,
        caption: filenameSanitized,
        filename: filenameSanitized,
    }
    );

    return (
        <div className={s.lazyImage} ref={reference}>
            <div
                ref={ref}
                style={{
                    width: "100%",
                    transition: "all 0.3s ease",
                    // opacity: loaded || disableOpacityAnimation ? 1 : 0,
                }}
                className={`${s.contentImage} ${loaded ? s.unblur : s.blur}`} // Apply unblur class when loaded is true
            >
                {fill ? (
                    <Image
                        alt={alt ? alt : filename ? filenameSanitized : ""}
                        src={src}
                        placeholder={"blur"}
                        blurDataURL={
                            lqip ? lqip : 'blur.png'// Use fallbackSrc if blurDataURL is not provided
                        }
                        fill={true}
                        objectFit={objectFit ? objectFit : "cover"}
                        sizes={sizes ? sizes : ""}
                        className={s.image}
                        onLoadingComplete={() => setLoaded(true)} // Set loaded to true when loading completes
                        style={{
                            transition: "all 0.3s ease",
                            opacity: 1,
                            aspectRatio: aspectRatio ? aspectRatio : "",
                        }}
                    />
                ) : (
                    <Image
                        alt={alt ? alt : filename ? filenameSanitized : ""}
                        src={src}
                        width={width}
                        height={height}
                        placeholder={"blur"}
                        blurDataURL={
                            lqip ? lqip : 'blur.png'// Use fallbackSrc if blurDataURL is not provided
                        }
                        className={s.image}
                        onLoadingComplete={() => setLoaded(true)} // Set loaded to true when loading completes
                        style={{
                            transition: "all 0.3s ease",
                            opacity: 1,
                            height: fullHeight ? "100%" : "",
                            width: fullWidth ? "100%" : "",
                            aspectRatio: aspectRatio ? aspectRatio : "",
                        }}
                    />
                )}
            </div>
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(imageSchema) }} />
        </div>
    );
}

